import { useRef, useState } from "react";
import { Play, Pause } from "lucide-react";

/**
 * Autoplaying editorial video that stays grayscale + muted as ambient background.
 * Clicking the play button reveals full color + audio; clicking again reverts to mono.
 */
export default function MonoVideo({
  src,
  poster,
  className = "",
  videoClassName = "h-full w-full object-cover",
  alt = "Editorial video",
  testId = "mono-video",
}) {
  const videoRef = useRef(null);
  const [live, setLive] = useState(false); // true = color + sound

  const toggle = () => {
    const v = videoRef.current;
    if (!v) return;
    if (!live) {
      v.muted = false;
      v.currentTime = 0;
      v.play().catch(() => {
        // If unmuted playback blocked, fall back to muted color playback
        v.muted = true;
        v.play().catch(() => {});
      });
      setLive(true);
    } else {
      v.muted = true;
      setLive(false);
    }
  };

  return (
    <div
      data-testid={testId}
      className={`relative overflow-hidden group bg-[#0a0a0a] ${className}`}
    >
      <video
        ref={videoRef}
        src={src}
        poster={poster}
        autoPlay
        muted
        loop
        playsInline
        preload="auto"
        aria-label={alt}
        className={`${videoClassName} transition-[filter] duration-700 ease-out ${
          live ? "" : "grayscale contrast-105"
        }`}
      />

      <button
        type="button"
        onClick={toggle}
        aria-label={live ? "Mute and grayscale" : "Play with color and sound"}
        data-testid={`${testId}-toggle`}
        className="absolute bottom-4 right-4 z-10 flex items-center gap-2 rounded-full bg-black/60 hover:bg-black backdrop-blur-sm text-white text-[10px] uppercase tracking-[0.25em] font-bold px-4 py-2.5 transition-all duration-300 opacity-90 hover:opacity-100 border border-white/20"
      >
        {live ? (
          <>
            <Pause size={12} strokeWidth={2.5} />
            <span>Mute</span>
          </>
        ) : (
          <>
            <Play size={12} strokeWidth={2.5} fill="currentColor" />
            <span>Play</span>
          </>
        )}
      </button>
    </div>
  );
}
